/**
 * Seções da integração WhatsApp (v6, admin-only) — conteúdo que morava na
 * antiga /whatsapp, agora REUSADO pela aba Comunicação de /settings/integrations:
 * (1) Conexão — api key da organização + registro do webhook;
 * (2) Conectar número — Embedded Signup da Meta;
 * (3) Números — canais conectados com vínculo de mesa e ativação;
 * (4) Contatos na Blip — o interruptor da spec 025 (só com número da Blip).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, CheckCircle2, Webhook } from 'lucide-react';
import {
  WHATSAPP_BOT_MODE_LABELS,
  WHATSAPP_CHANNEL_NAME_MAX,
  WHATSAPP_PROVIDER_LABELS,
  WhatsappProviderSchema,
  whatsappChannelLabel,
  type WhatsappChannel,
  type WhatsappChannelPatchBody,
  type WhatsappIntegration,
  type WhatsappIntegrationPutBody,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, EmptyState, Skeleton } from '@/components/ui/misc';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { Section } from '@/features/settings/section';
import { BlipSwitchSection } from './blip-switch-section';
import { ChatbotDialog } from './chatbot-dialog';
import { EmbeddedSignup } from './EmbeddedSignup';
import {
  WHATSAPP_KEY,
  errorMessage,
  whatsappChannelsOptions,
  whatsappConfigOptions,
  whatsappIntegrationOptions,
} from './whatsapp-queries';

/** Pilha completa de gerenciamento do canal WhatsApp (admin-only na API). */
export function WhatsappSections() {
  const [integrationQ, configQ, channelsQ] = useQueries({
    queries: [whatsappIntegrationOptions(), whatsappConfigOptions(), whatsappChannelsOptions()],
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Conecte números do WhatsApp Business à plataforma: as conversas chegam em Chats, na mesa
        vinculada a cada número.
      </p>

      {/* ── 1. Conexão ──────────────────────────────────────────────────── */}
      <Section
        title="Conexão"
        description="API key da organização no zeepchat (zeep_...) e registro do webhook de entrada."
      >
        {integrationQ.isPending ? (
          <div className="space-y-2" aria-busy>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
          </div>
        ) : integrationQ.isError ? (
          <EmptyState message={`Erro ao carregar a integração: ${errorMessage(integrationQ.error)}`} />
        ) : (
          <IntegrationSection integration={integrationQ.data} />
        )}
      </Section>

      {/* ── 2. Conectar número ──────────────────────────────────────────── */}
      <Section
        title="Conectar número"
        description="Embedded Signup da Meta: autorize com o Facebook da empresa para conectar (ou migrar) um número."
      >
        {integrationQ.data && integrationQ.data.apiKeyMasked === null ? (
          <p className="mb-3 text-xs text-muted-foreground">
            Salve a API key da organização acima antes de conectar um número — o cadastro do número
            é feito no broker com essa chave.
          </p>
        ) : null}
        {configQ.isPending ? (
          <Skeleton className="h-24 w-full" aria-busy />
        ) : configQ.isError ? (
          <EmptyState message={`Erro ao carregar a configuração: ${errorMessage(configQ.error)}`} />
        ) : (
          <EmbeddedSignup config={configQ.data} />
        )}
      </Section>

      {/* ── 3. Números ──────────────────────────────────────────────────── */}
      <Section
        title="Números"
        description="Números conectados: vincule cada um à mesa que recebe as conversas. O nome e o provedor aparecem na conversa, para quem atende saber por qual número a pessoa fala."
      >
        {channelsQ.isPending ? (
          <div className="space-y-2" aria-busy>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : channelsQ.isError ? (
          <EmptyState message={`Erro ao carregar os números: ${errorMessage(channelsQ.error)}`} />
        ) : (channelsQ.data ?? []).length === 0 ? (
          <EmptyState message="Nenhum número conectado ainda" />
        ) : (
          <ChannelsTable channels={channelsQ.data ?? []} />
        )}
      </Section>

      {/* ── 4. Contatos na Blip (spec 025, fase 3) ──────────────────────── */}
      {(channelsQ.data ?? []).some((c) => c.provider === 'blip') ? (
        <Section
          title="Contatos na Blip"
          description="O disparo pela Blip marca o contato no roteador para a resposta cair no nosso bot. Aqui fica o interruptor geral: parar os disparos pela Blip e devolver todos os contatos ao fluxo da Blip."
        >
          <BlipSwitchSection />
        </Section>
      ) : null}
    </div>
  );
}

// ── 1. Conexão: api key + webhook ───────────────────────────────────────────

function IntegrationSection({ integration }: { integration: WhatsappIntegration }) {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [businessId, setBusinessId] = useState(integration.businessId ?? '');
  const [saved, setSaved] = useState(false);

  // Edições fora do submit (ex.: refetch) não devem sobrescrever o form em
  // digitação — sincroniza só o businessId quando o servidor mudar.
  useEffect(() => {
    setBusinessId(integration.businessId ?? '');
  }, [integration.businessId]);

  const save = useMutation({
    mutationFn: async (body: WhatsappIntegrationPutBody) => {
      const res = await api<WhatsappIntegration>('/whatsapp/integration', { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      setApiKey('');
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: [...WHATSAPP_KEY, 'integration'] });
    },
  });

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 3000);
    return () => clearTimeout(t);
  }, [saved]);

  const registerWebhook = useMutation({
    mutationFn: async () => {
      const res = await api<unknown>('/whatsapp/integration/webhook', { method: 'POST' });
      if (res.error) throw res.error;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: [...WHATSAPP_KEY, 'integration'] }),
  });

  const configured = integration.apiKeyMasked !== null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const key = apiKey.trim();
    if (!key) return;
    save.mutate({ apiKey: key, businessId: businessId.trim() || undefined });
  };

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="wa-api-key">
              API key da organização {configured ? null : <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="wa-api-key"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                configured ? `Configurada (${integration.apiKeyMasked}) — cole para trocar` : 'zeep_...'
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-business-id">Business ID (opcional)</Label>
            <Input
              id="wa-business-id"
              value={businessId}
              onChange={(e) => setBusinessId(e.target.value)}
              placeholder="ID do portfólio empresarial na Meta"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={save.isPending || apiKey.trim() === ''}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
          {saved ? (
            <span className="flex items-center gap-1 text-xs text-[#52C41A]">
              <CheckCircle2 className="size-3.5" />
              Integração salva.
            </span>
          ) : null}
          {save.error ? (
            <span className="text-xs text-destructive" role="alert">
              {errorMessage(save.error)}
            </span>
          ) : null}
        </div>
      </form>

      <div className="flex flex-wrap items-center gap-3 border-t pt-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!configured || registerWebhook.isPending}
          onClick={() => registerWebhook.mutate()}
          title={configured ? undefined : 'Salve a API key antes de registrar o webhook'}
        >
          <Webhook className="size-4" />
          {registerWebhook.isPending ? 'Registrando…' : 'Registrar webhook'}
        </Button>
        {/* O badge é a verdade persistida (webhookRegistered do GET): a mutation
            invalida a query e o estado vem do servidor — undefined conta como
            pendente. */}
        {integration.webhookRegistered ? (
          <Badge variant="success">Webhook registrado</Badge>
        ) : (
          <Badge variant="warning">Webhook pendente</Badge>
        )}
        {registerWebhook.error ? (
          <span className="text-xs text-destructive" role="alert">
            {errorMessage(registerWebhook.error)}
          </span>
        ) : null}
        <p className="w-full text-xs text-muted-foreground">
          O webhook aponta o broker para esta plataforma — registre uma vez após salvar a API key (e
          novamente se a URL pública da API mudar).
        </p>
      </div>
    </div>
  );
}

// ── 3. Números: tabela de canais ────────────────────────────────────────────

function ChannelsTable({ channels }: { channels: WhatsappChannel[] }) {
  const queryClient = useQueryClient();
  const desksQ = useQuery(desksListQuery());
  const desks = (desksQ.data ?? []).filter((d) => d.active);
  const [error, setError] = useState<string | null>(null);
  // Canal com o dialog do chatbot aberto (v8 Etapa 4).
  const [chatbotChannel, setChatbotChannel] = useState<WhatsappChannel | null>(null);

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WhatsappChannelPatchBody }) => {
      const res = await api<WhatsappChannel>(`/whatsapp/channels/${id}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: [...WHATSAPP_KEY, 'channels'] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted text-left text-[13px]">
            <th className="h-9 px-4 font-semibold">Número</th>
            <th className="h-9 w-48 px-4 font-semibold">Nome na conversa</th>
            <th className="h-9 w-28 px-4 font-semibold">Provedor</th>
            <th className="h-9 px-4 font-semibold">Phone number ID</th>
            <th className="h-9 w-56 px-4 font-semibold">Mesa</th>
            <th className="h-9 px-4 font-semibold">Bot</th>
            <th className="h-9 w-20 px-4 font-semibold">Ativo</th>
          </tr>
        </thead>
        <tbody>
          {channels.map((c) => (
            <tr key={c.id} className="border-b border-divider transition-colors last:border-0 hover:bg-accent/60">
              <td className="px-4 py-2.5 font-medium whitespace-nowrap">
                {c.displayPhone}
                {/* spec 028: como a conversa mostra este número */}
                <span className="block text-[11px] font-normal text-muted-foreground">
                  {whatsappChannelLabel({ name: c.name ?? null, provider: c.provider ?? 'meta', displayPhone: c.displayPhone })}
                </span>
              </td>
              <td className="px-4 py-2">
                <ChannelNameInput
                  channel={c}
                  disabled={update.isPending}
                  onSave={(name) => update.mutate({ id: c.id, body: { name } })}
                />
              </td>
              <td className="px-4 py-2">
                <Select
                  aria-label={`Provedor do número ${c.displayPhone}`}
                  className="h-8"
                  value={c.provider ?? 'meta'}
                  disabled={update.isPending}
                  onChange={(e) =>
                    update.mutate({ id: c.id, body: { provider: WhatsappProviderSchema.parse(e.target.value) } })
                  }
                >
                  {WhatsappProviderSchema.options.map((p) => (
                    <option key={p} value={p}>
                      {WHATSAPP_PROVIDER_LABELS[p]}
                    </option>
                  ))}
                </Select>
              </td>
              <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                {c.phoneNumberId}
              </td>
              <td className="px-4 py-2">
                <Select
                  aria-label={`Mesa do número ${c.displayPhone}`}
                  className="h-8"
                  value={c.deskId !== null ? String(c.deskId) : ''}
                  disabled={desksQ.isPending || update.isPending}
                  onChange={(e) =>
                    update.mutate({
                      id: c.id,
                      body: { deskId: e.target.value === '' ? null : Number(e.target.value) },
                    })
                  }
                >
                  <option value="">Sem mesa</option>
                  {desks.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.displayName}
                    </option>
                  ))}
                </Select>
              </td>
              <td className="px-4 py-2">
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label={`Configurar bot do número ${c.displayPhone}`}
                    title="Configurar bot"
                    onClick={() => setChatbotChannel(c)}
                  >
                    <Bot />
                  </Button>
                  <span className="text-xs text-muted-foreground">{chatbotSummary(c)}</span>
                </span>
              </td>
              <td className="px-4 py-2.5">
                <Switch
                  aria-label={`Número ${c.displayPhone} ativo`}
                  checked={c.active}
                  disabled={update.isPending}
                  onCheckedChange={(active) => update.mutate({ id: c.id, body: { active } })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error ? (
        <p className="px-1 pt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {chatbotChannel ? (
        <ChatbotDialog channel={chatbotChannel} onClose={() => setChatbotChannel(null)} />
      ) : null}
    </div>
  );
}

/**
 * O nome do número na conversa (spec 028): salva ao sair do campo ou no Enter;
 * vazio volta a mostrar só o número.
 */
function ChannelNameInput({
  channel,
  disabled,
  onSave,
}: {
  channel: WhatsappChannel;
  disabled: boolean;
  onSave: (name: string | null) => void;
}) {
  const [value, setValue] = useState(channel.name ?? '');
  // o servidor mudou (outra aba, refetch): acompanha, sem atropelar a digitação
  useEffect(() => setValue(channel.name ?? ''), [channel.name]);
  const commit = () => {
    const next = value.trim() === '' ? null : value.trim();
    if (next !== (channel.name ?? null)) onSave(next);
  };
  return (
    <Input
      aria-label={`Nome do número ${channel.displayPhone} na conversa`}
      className="h-8"
      value={value}
      maxLength={WHATSAPP_CHANNEL_NAME_MAX}
      placeholder="Ex.: Atendimento"
      disabled={disabled}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
    />
  );
}

/**
 * Indicador compacto do bot na linha (spec 021): "Sem bot" | "Chatbot do ERP ·
 * 3 opções" | "Bot externo (Zeepchat) · 2 filas".
 */
function chatbotSummary(c: WhatsappChannel): string {
  // ?? 'none': resposta de uma API anterior à 021 durante o deploy
  const mode = c.botMode ?? (c.chatbotEnabled ? 'erp' : 'none');
  const label = WHATSAPP_BOT_MODE_LABELS[mode];
  if (mode === 'erp') {
    const count = c.chatbotMenu?.length ?? 0;
    return `${label} · ${count} ${count === 1 ? 'opção' : 'opções'}`;
  }
  if (mode === 'external') {
    const count = c.botHandoffQueues?.length ?? 0;
    return count === 0 ? label : `${label} · ${count} ${count === 1 ? 'fila' : 'filas'}`;
  }
  return label;
}
